import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  TrendingUp, 
  TrendingDown,
  Zap,
  Wrench,
  Users,
  Calendar,
  BarChart3,
  PieChart,
  MessageCircle
} from 'lucide-react';
import { useAuth0Context } from '../contexts/Auth0Context';
import ThemedCard from '../components/ThemedCard';
import ThemedButton from '../components/ThemedButton';
import SystemStatus from '../components/SystemStatus';
import { PROCOGIA_COLORS, getTextStyles } from '../utils/themeUtils';

const OverviewDashboard = () => {
  const { appUser } = useAuth0Context();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    try {
      // Simulate comprehensive dashboard data
      setStats({
        totalEquipment: 700,
        activeEquipment: 529,
        criticalAlerts: 12,
        pendingMaintenance: 45,
        efficiency: 87.5,
        uptime: 96.2,
        costSavings: 125000,
        predictedIssues: 8
      });

      setChartData({
        equipmentStatus: [
          { name: 'Operational', value: 529, color: PROCOGIA_COLORS.green },
          { name: 'Warning', value: 132, color: PROCOGIA_COLORS.lightGrey },
          { name: 'Critical', value: 39, color: PROCOGIA_COLORS.darkGrey }
        ],
        maintenanceSchedule: [
          { month: 'Jan', scheduled: 45, completed: 42, overdue: 3 },
          { month: 'Feb', scheduled: 52, completed: 48, overdue: 4 },
          { month: 'Mar', scheduled: 38, completed: 35, overdue: 3 },
          { month: 'Apr', scheduled: 61, completed: 58, overdue: 3 },
          { month: 'May', scheduled: 45, completed: 43, overdue: 2 },
          { month: 'Jun', scheduled: 55, completed: 50, overdue: 5 }
        ],
        efficiencyTrend: [
          { date: '2024-01', efficiency: 84.2 },
          { date: '2024-02', efficiency: 85.1 },
          { date: '2024-03', efficiency: 86.8 },
          { date: '2024-04', efficiency: 85.9 },
          { date: '2024-05', efficiency: 87.2 },
          { date: '2024-06', efficiency: 87.5 }
        ]
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching overview data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ icon, title, value, change, changeType, iconType = "success" }) => (
    <ThemedCard 
      icon={icon}
      iconType={iconType}
      title={title}
      value={value}
      change={change}
      changeType={changeType}
    />
  );

  const SimpleBarChart = ({ data, title }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={index} className="flex items-center">
            <div className="w-16 text-sm text-gray-600">{item.month}</div>
            <div className="flex-1 ml-4">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full" 
                    style={{ 
                      width: `${(item.completed / item.scheduled) * 100}%`,
                      backgroundColor: PROCOGIA_COLORS.green
                    }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600">
                  {item.completed}/{item.scheduled}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const DonutChart = ({ data, title }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="flex items-center justify-center">
        <div className="relative">
          <svg width="200" height="200" className="transform -rotate-90">
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="20"
            />
            {data.map((item, index) => {
              const total = data.reduce((sum, d) => sum + d.value, 0);
              const percentage = (item.value / total) * 100;
              const strokeDasharray = `${(percentage / 100) * 502.4} 502.4`;
              const strokeDashoffset = index === 0 ? 0 : 
                -data.slice(0, index).reduce((sum, d) => sum + (d.value / total) * 502.4, 0);
              
              return (
                <circle
                  key={index}
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke={item.color}
                  strokeWidth="20"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{stats.totalEquipment}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-2" 
                style={{ backgroundColor: item.color }}
              ></div>
              <span className="text-sm text-gray-600">{item.name}</span>
            </div>
            <span className="text-sm font-medium text-gray-900">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Overview Dashboard</h1>
          <p className="text-gray-600">Welcome back, {appUser?.name || 'User'}! Here's your system overview.</p>
        </div>

      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Activity}
          title="Equipment Online"
          value={`${stats.activeEquipment}/${stats.totalEquipment}`}
          change="+2.3%"
          changeType="positive"
          color="green"
        />
        <StatCard
          icon={AlertTriangle}
          title="Critical Alerts"
          value={stats.criticalAlerts}
          change="-1.2%"
          changeType="positive"
          color="red"
        />
        <StatCard
          icon={Wrench}
          title="Pending Maintenance"
          value={stats.pendingMaintenance}
          change="+5.1%"
          changeType="negative"
          color="orange"
        />
        <StatCard
          icon={TrendingUp}
          title="System Efficiency"
          value={`${stats.efficiency}%`}
          change="+1.8%"
          changeType="positive"
          color="blue"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Zap}
          title="Uptime"
          value={`${stats.uptime}%`}
          change="+0.5%"
          changeType="positive"
          color="purple"
        />
        <StatCard
          icon={TrendingUp}
          title="Cost Savings"
          value={`$${stats.costSavings.toLocaleString()}`}
          change="+12.3%"
          changeType="positive"
          color="green"
        />
        <StatCard
          icon={AlertTriangle}
          title="Predicted Issues"
          value={stats.predictedIssues}
          change="-8.7%"
          changeType="positive"
          color="yellow"
        />
        <StatCard
          icon={CheckCircle}
          title="Maintenance Score"
          value="A+"
          change="Improved"
          changeType="positive"
          color="indigo"
        />
      </div>

      {/* Charts and System Status Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DonutChart 
          data={chartData.equipmentStatus} 
          title="Equipment Status Distribution"
        />
        <SimpleBarChart 
          data={chartData.maintenanceSchedule} 
          title="Maintenance Completion Rate"
        />
        <SystemStatus />
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {[
            { type: 'maintenance', message: 'Preventive maintenance completed on Pump #127', time: '2 hours ago', status: 'completed' },
            { type: 'alert', message: 'High vibration detected on Motor #45', time: '4 hours ago', status: 'warning' },
            { type: 'prediction', message: 'AI predicted bearing failure in Compressor #89', time: '6 hours ago', status: 'predicted' },
            { type: 'maintenance', message: 'Emergency repair on Conveyor Belt #12', time: '1 day ago', status: 'emergency' },
            { type: 'success', message: 'System efficiency improved by 2.3%', time: '2 days ago', status: 'success' }
          ].map((activity, index) => (
            <div key={index} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: activity.status === 'completed' ? '#e8f5e8' :
                                   activity.status === 'warning' ? '#fff3cd' :
                                   activity.status === 'predicted' ? '#e6f7fa' :
                                   activity.status === 'emergency' ? '#f8d7da' :
                                   '#e8f5e8',
                  color: activity.status === 'completed' ? PROCOGIA_COLORS.green :
                         activity.status === 'warning' ? '#856404' :
                         activity.status === 'predicted' ? PROCOGIA_COLORS.blue :
                         activity.status === 'emergency' ? '#721c24' :
                         PROCOGIA_COLORS.green
                }}
              >
                {activity.status === 'completed' ? <CheckCircle className="w-4 h-4" /> :
                 activity.status === 'warning' ? <AlertTriangle className="w-4 h-4" /> :
                 activity.status === 'predicted' ? <TrendingUp className="w-4 h-4" /> :
                 activity.status === 'emergency' ? <Wrench className="w-4 h-4" /> :
                 <CheckCircle className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">{activity.message}</p>
                <p className="text-xs text-gray-500">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>


    </div>
  );
};

export default OverviewDashboard; 